import { useState, useEffect } from 'react';
import { usePageVisit, useFormSubmissionTracking } from '../../hooks/useAnalytics';
import TimeSimulator from '../../components/TimeSimulator';

export default function HomePage() {
  const [formLoaded, setFormLoaded] = useState(false);

  usePageVisit('home');
  useFormSubmissionTracking();

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://epicrealestate.kit.com/f23c5215b1/index.js';
    script.async = true;
    script.setAttribute('data-uid', 'f23c5215b1');

    script.onload = () => {
      setFormLoaded(true);
      setTimeout(() => {
        const formContainer = document.querySelector('[data-form-container]');
        const convertKitForm = document.querySelector('.formkit-form[data-uid="f23c5215b1"]');
        if (formContainer && convertKitForm && !formContainer.contains(convertKitForm)) {
          formContainer.appendChild(convertKitForm);
        }
      }, 500);
    };

    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const scrollToForm = () => {
    const formSection = document.getElementById('download-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="bg-[#0A1628] text-white min-h-screen" style={{ fontFamily: 'Montserrat, sans-serif' }}>

      {/* HERO — Time Machine Simulator */}
      <section className="relative min-h-screen flex items-center justify-center py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#1A1A1A] to-[#0A1628]"></div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 w-full">
          <TimeSimulator onOptInClick={scrollToForm} />
        </div>
      </section>

      {/* Why You Need This Section */}
      <section className="relative py-16 md:py-24 bg-[#1A1A1A]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-[32px] md:text-[48px] lg:text-[56px] font-black text-white mb-4 md:mb-6" style={{ fontFamily: 'Oswald, sans-serif' }}>
              WHY YOU NEED THIS<br />
              <span className="text-[#D4AF37]">PROTOCOL</span>
            </h2>
            <p className="text-[15px] md:text-[18px] text-white/80 max-w-[800px] mx-auto leading-[1.7]">
              Gold dealers use psychological warfare to separate you from your wealth. This checklist is your perimeter defense.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-[#0A1628] border-2 border-[#FF0000] rounded-lg p-6 md:p-8 hover:transform hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-[#FF0000] rounded-full flex items-center justify-center mb-6">
                <i className="ri-alert-line text-3xl text-white"></i>
              </div>
              <h3 className="text-[20px] md:text-[24px] font-bold text-white mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
                EXPOSE THE "SPREAD" TRAP
              </h3>
              <p className="text-[14px] md:text-[15px] text-white/70 leading-[1.7]">
                Predatory dealers hide massive commissions inside the "Spread." They charge you 30-50% over Spot Price for "collectible" coins that are impossible to liquidate. This guide forces them to disclose the real spread or hang up.
              </p>
            </div>

            <div className="bg-[#0A1628] border-2 border-[#D4AF37] rounded-lg p-6 md:p-8 hover:transform hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-cross-line text-3xl text-[#1A1A1A]"></i>
              </div>
              <h3 className="text-[20px] md:text-[24px] font-bold text-white mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
                AVOID THE "NUMISMATIC" LIE
              </h3>
              <p className="text-[14px] md:text-[15px] text-white/70 leading-[1.7]">
                They will tell you "Bullion is dangerous" to sell you overpriced Numismatic Coins. On a $50,000 purchase, avoiding this single lie saves you $15,000+ in immediate equity.
              </p>
            </div>

            <div className="bg-[#0A1628] border-2 border-[#00FF00] rounded-lg p-6 md:p-8 hover:transform hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-[#00FF00] rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-[#1A1A1A]"></i>
              </div>
              <h3 className="text-[20px] md:text-[24px] font-bold text-white mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
                IDENTIFY THE GOOD GUYS
              </h3>
              <p className="text-[14px] md:text-[15px] text-white/70 leading-[1.7]">
                Legitimate dealers run on tight margins and transparency. Scammers rely on confusion. Audit any dealer in 60 seconds using these specific questions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside — 4 Tactical Categories */}
      <section className="relative py-16 md:py-24 bg-[#0A1628]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-[32px] md:text-[48px] lg:text-[56px] font-black text-white mb-4 md:mb-6" style={{ fontFamily: 'Oswald, sans-serif' }}>
              INTEL INSIDE:<br />
              <span className="text-[#D4AF37]">THE 4 TACTICAL CATEGORIES</span>
            </h2>
            <p className="text-[15px] md:text-[18px] text-white/80 max-w-[800px] mx-auto leading-[1.7]">
              10 questions designed to neutralize high-pressure sales scripts instantly.
            </p>
          </div>

          <div className="space-y-6 md:space-y-8">
            <div className="bg-[#1A1A1A] border-l-4 border-[#FF0000] rounded-lg p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#FF0000] rounded-lg flex items-center justify-center">
                  <span className="text-white text-xl font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-[20px] md:text-[24px] font-bold text-white mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    🛑 THE "SPREAD" &amp; FEE TRAPS (Questions 1-3)
                  </h3>
                  <p className="text-[14px] md:text-[15px] text-white/70 leading-[1.7]">
                    Uncover the invisible costs. We give you the exact script to ask: "What is your buy-back price right now?" This instantly reveals the true Bid-Ask Spread and exposes if you are buying an investment or a liability.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1A1A1A] border-l-4 border-[#D4AF37] rounded-lg p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                  <span className="text-[#1A1A1A] text-xl font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-[20px] md:text-[24px] font-bold text-white mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    🛑 THE "NUMISMATIC" &amp; CONFISCATION TRAPS (Questions 4-5)
                  </h3>
                  <p className="text-[14px] md:text-[15px] text-white/70 leading-[1.7]">
                    Destroy the "Government Confiscation" myth used to push overpriced coins. Learn to distinguish between low-premium Bullion (Investment Grade) and high-markup "Proof" coins.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1A1A1A] border-l-4 border-[#00FF00] rounded-lg p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#00FF00] rounded-lg flex items-center justify-center">
                  <span className="text-[#1A1A1A] text-xl font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-[20px] md:text-[24px] font-bold text-white mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    🛑 THE "POSSESSION" TRAPS (Questions 6-7)
                  </h3>
                  <p className="text-[14px] md:text-[15px] text-white/70 leading-[1.7]">
                    Verify physical inventory. Ensure they aren't running a "Fractional Reserve" scheme with your money. Confirm distinct Allocated Storage vs. commingled assets.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1A1A1A] border-l-4 border-[#FF0000] rounded-lg p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#FF0000] rounded-lg flex items-center justify-center">
                  <span className="text-white text-xl font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-[20px] md:text-[24px] font-bold text-white mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    🛑 THE "AUTHORITY" TRAPS (Questions 8-10)
                  </h3>
                  <p className="text-[14px] md:text-[15px] text-white/70 leading-[1.7]">
                    Force them to put promises in writing. Confirm quality testing, verify Tier 1 Capital backing, and expose commission-breath sales incentives.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={scrollToForm}
              className="bg-[#D4AF37] hover:bg-[#FFD700] text-[#1A1A1A] px-8 md:px-12 py-4 md:py-5 rounded-lg text-[16px] md:text-[18px] font-bold transition-all duration-300 transform hover:scale-105 shadow-lg cursor-pointer whitespace-nowrap"
            >
              GET THE DEALER INTERROGATION GUIDE
            </button>
          </div>
        </div>
      </section>

      {/* Price Comparison — Two Receipts */}
      <section className="relative py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A] via-[#252525] to-[#1A1A1A]"></div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-[32px] md:text-[48px] lg:text-[56px] font-black text-white mb-4 md:mb-6" style={{ fontFamily: 'Oswald, sans-serif' }}>
              THE REAL COST OF<br />
              <span className="text-[#FF0000]">NOT ASKING</span>
            </h2>
            <p className="text-[15px] md:text-[18px] text-white/80 max-w-[800px] mx-auto leading-[1.7]">
              Same gold content. Same weight. Wildly different outcomes. Here is the math on a $50k purchase:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 max-w-[1000px] mx-auto">
            {/* Scam Dealer */}
            <div className="bg-[#1A1A1A] border-2 border-[#FF0000] rounded-lg p-6 md:p-8 relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FF0000] px-8 py-2 rounded-full flex items-center justify-center min-w-[200px]">
                <span className="text-white font-bold text-xs sm:text-sm text-center">THE "SCAM" DEALER</span>
              </div>
              <div className="mt-4 space-y-4">
                <div className="border-b border-white/10 pb-4">
                  <h3 className="text-[20px] md:text-[24px] font-bold text-white mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    "Rare" Proof Gold Eagles
                  </h3>
                  <p className="text-sm text-white/60">Numismatic Trap</p>
                  <p className="text-xs text-white/50 mt-1">(Hard to sell, non-standard)</p>
                </div>
                <div className="space-y-3 text-white/80">
                  <div className="flex justify-between text-sm">
                    <span>Gold Spot Price (per oz)</span><span>$2,650</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Dealer Markup (Hidden Premium)</span><span className="text-[#FF0000]">+$1,150/oz</span>
                  </div>
                  <div className="flex justify-between text-xs text-white/60">
                    <span>(43% Premium)</span><span></span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-white/10 pt-3">
                    <span>"Processing Fees" (Junk Fee)</span><span className="text-[#FF0000]">+$500</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>"Insurance" (Padding)</span><span className="text-[#FF0000]">+$495</span>
                  </div>
                </div>
                <div className="border-t-2 border-[#FF0000] pt-4 mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-lg font-bold text-white">YOUR TOTAL COST</span>
                    <span className="text-[28px] md:text-[32px] font-black text-[#FF0000]" style={{ fontFamily: 'Oswald, sans-serif' }}>$38,995</span>
                  </div>
                  <div className="flex justify-between text-xs text-white/50 mb-1">
                    <span>(for 10 oz)</span><span></span>
                  </div>
                  <div className="flex justify-between text-sm text-white/60 mb-2">
                    <span>Actual Gold Value</span><span>$26,500</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-bold text-[#FF0000]">IMMEDIATE LOSS</span>
                    <span className="font-bold text-[#FF0000]">❌ -$12,495 (32%)</span>
                  </div>
                </div>
                <div className="bg-[#FF0000]/10 border border-[#FF0000] rounded-lg p-4 mt-4">
                  <p className="text-xs text-white/70 leading-relaxed">
                    <strong className="text-[#FF0000]">What they won't tell you:</strong> Those "rare" coins are mass-produced. The premium is pure profit. You lose 32% of your wealth the second you sign.
                  </p>
                </div>
              </div>
            </div>

            {/* Honest Dealer */}
            <div className="bg-[#1A1A1A] border-2 border-[#00FF00] rounded-lg p-6 md:p-8 relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#00FF00] px-8 py-2 rounded-full flex items-center justify-center min-w-[200px]">
                <span className="text-[#1A1A1A] font-bold text-xs sm:text-sm text-center">THE HONEST DEALER</span>
              </div>
              <div className="mt-4 space-y-4">
                <div className="border-b border-white/10 pb-4">
                  <h3 className="text-[20px] md:text-[24px] font-bold text-white mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    Standard American Gold Eagles
                  </h3>
                  <p className="text-sm text-white/60">Bullion Standard</p>
                  <p className="text-xs text-white/50 mt-1">(Liquid, standard investment)</p>
                </div>
                <div className="space-y-3 text-white/80">
                  <div className="flex justify-between text-sm">
                    <span>Gold Spot Price (per oz)</span><span>$2,650</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Dealer Markup (Fair Spread)</span><span className="text-[#00FF00]">+$80/oz</span>
                  </div>
                  <div className="flex justify-between text-xs text-white/60">
                    <span>(3% Spread)</span><span></span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-white/10 pt-3">
                    <span>"Processing Fees" (Transparent)</span><span className="text-white/60">$0</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>"Insurance" (Actual Cost)</span><span className="text-white/60">$50</span>
                  </div>
                </div>
                <div className="border-t-2 border-[#00FF00] pt-4 mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-lg font-bold text-white">YOUR TOTAL COST</span>
                    <span className="text-[28px] md:text-[32px] font-black text-[#00FF00]" style={{ fontFamily: 'Oswald, sans-serif' }}>$27,350</span>
                  </div>
                  <div className="flex justify-between text-xs text-white/50 mb-1">
                    <span>(for 10 oz)</span><span></span>
                  </div>
                  <div className="flex justify-between text-sm text-white/60 mb-2">
                    <span>Actual Gold Value</span><span>$26,500</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-bold text-[#00FF00]">IMMEDIATE LOSS</span>
                    <span className="font-bold text-[#00FF00]">✅ -$850 (3.1%)</span>
                  </div>
                </div>
                <div className="bg-[#00FF00]/10 border border-[#00FF00] rounded-lg p-4 mt-4">
                  <p className="text-xs text-white/70 leading-relaxed">
                    <strong className="text-[#00FF00]">What honest dealers do:</strong> Transparent pricing. Minimal markup. No BS fees. They answer every question on the checklist.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Savings Callout */}
          <div className="mt-12 md:mt-16 text-center">
            <div className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#FFD700] px-8 md:px-12 py-6 md:py-8 rounded-lg">
              <p className="text-sm md:text-base text-[#1A1A1A] font-bold mb-2">YOU SAVE</p>
              <p className="text-[40px] md:text-[56px] font-black text-[#1A1A1A]" style={{ fontFamily: 'Oswald, sans-serif' }}>$11,645</p>
              <p className="text-xs md:text-sm text-[#1A1A1A]/80 mt-2">On a single tube of coins. Just by asking the right questions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA — ConvertKit Form */}
      <section id="download-form" className="relative py-16 md:py-24 bg-[#0A1628]">
        <div className="max-w-[800px] mx-auto px-4 md:px-6 text-center">
          <h2 className="text-[32px] md:text-[48px] lg:text-[56px] font-black text-white mb-4 md:mb-6" style={{ fontFamily: 'Oswald, sans-serif' }}>
            PROTECT YOUR PERIMETER.
          </h2>
          <p className="text-[16px] md:text-[18px] text-white/90 mb-8 md:mb-12 leading-[1.7]">
            Don't be a statistic. Get the questions that scammers are terrified to answer.
          </p>

          <div className="mb-8 md:mb-10 flex justify-center">
            <div className="w-64 h-64 md:w-80 md:h-80 flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/93c3e079cd1f297eadf6098c3b0ef55d/2a6cc4d425cb09463763de866c7e3b61.png"
                alt="Gold Dealer Vetting Checklist Preview"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 md:p-10 shadow-2xl">
            {!formLoaded && (
              <div className="min-h-[400px] flex items-center justify-center">
                <p className="text-gray-500">Loading form...</p>
              </div>
            )}
            <div data-form-container className="min-h-[200px]"></div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1628] border-t border-[#D4AF37]/20 py-8 md:py-12">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-0">
            <div className="flex items-center gap-3 md:gap-4">
              <img
                src="https://static.readdy.ai/image/93c3e079cd1f297eadf6098c3b0ef55d/031439dfb7c5b3457d1186c629c535e0.png"
                alt="Shield Logo"
                className="h-8 w-8 md:h-10 md:w-10 object-contain"
              />
              <p className="text-xs text-white/70">Epic Real Estate</p>
            </div>
            <div className="flex items-center gap-4 md:gap-8">
              <a href="/privacy-policy" className="text-xs md:text-sm text-white/70 hover:text-[#D4AF37] transition-colors whitespace-nowrap">
                Privacy Policy
              </a>
              <a href="/terms-and-conditions" className="text-xs md:text-sm text-white/70 hover:text-[#D4AF37] transition-colors whitespace-nowrap">
                Terms &amp; Conditions
              </a>
              <a href="/admin" className="text-xs md:text-sm text-white/35 hover:text-[#D4AF37] transition-colors whitespace-nowrap">
                Admin
              </a>
            </div>
          </div>
          <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-white/10 text-center">
            <p className="text-[10px] md:text-xs text-white/50">
              © 2026 Epic Real Estate. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
